import React, { useState, useEffect, useCallback, useRef } from "react";
import { db } from "../firebase";
import { collection, doc, getDoc, getDocs, setDoc, query, where, Timestamp } from "firebase/firestore";
import { defaultSettings, defaultShiftPatterns } from "../utils/payroll";

// シフト表。月ごとに「社員 × 日」のマス目で勤務パターンを割り当てる。
// 画面で1マスずつ編集する方法と、CSVを取り込んで一括で反映する方法の両方に対応する。
// ここで決めた勤務パターンが、その日の所定労働時間として給与計算に使われる。

const DOW = ["日", "月", "火", "水", "木", "金", "土"];
const pad = (n) => String(n).padStart(2, "0");
const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
};
const daysInMonth = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m, 0).getDate();
};
const docIdOf = (companyId, ym) => `${companyId}_${ym}`;

// マスの値は「勤務パターンのkey（文字列）」か「時間を直接指定した object」のどちらか。
const isCustom = (v) => v && typeof v === "object";
const mmToHHMM = (mm) => `${pad(Math.floor((mm % 1440) / 60))}:${pad(mm % 60)}`;
const hhmmToMm = (t) => {
  if (!t || !/^\d{1,2}:\d{2}$/.test(t)) return null;
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};
/** 実働時間（分）。終了が開始以下なら翌日にまたがるものとして扱う。 */
const workMinutesOf = (v) => {
  const s = hhmmToMm(v.start), e = hhmmToMm(v.end);
  if (s === null || e === null) return 0;
  const span = e > s ? e - s : (24 * 60 - s) + e;
  return Math.max(0, span - (Number(v.breakMin) || 0));
};
const customLabel = (v) => `${v.start}-${v.end}`;

function ShiftTable({ user, companyId, canEdit }) {
  const [yearMonth, setYearMonth] = useState(thisMonth);
  const [patterns, setPatterns]   = useState(defaultShiftPatterns());
  const [employees, setEmployees] = useState([]);
  const [cells, setCells]         = useState({});   // { uid: { day: patternKey } }
  const [loading, setLoading]     = useState(true);
  const [busy, setBusy]           = useState("");
  const [error, setError]         = useState("");
  const [message, setMessage]     = useState("");
  const [dirty, setDirty]         = useState(false);
  const [mode, setMode]           = useState("pattern");   // pattern | time
  const [view, setView]           = useState("month");     // month | day
  const [day, setDay]             = useState(new Date().getDate());
  const [editing, setEditing]     = useState(null);        // { uid, day, value }
  const fileRef = useRef();

  const load = useCallback(async () => {
    setLoading(true); setError(""); setMessage("");
    try {
      const sSnap = await getDoc(doc(db, "payrollSettings", companyId));
      const st = sSnap.exists() ? { ...defaultSettings(companyId), ...sSnap.data() } : defaultSettings(companyId);
      setPatterns(Array.isArray(st.shiftPatterns) && st.shiftPatterns.length > 0
        ? st.shiftPatterns : defaultShiftPatterns());

      const uSnap = await getDocs(query(
        collection(db, "users"),
        where("companyId", "==", companyId),
        where("status", "==", "approved")
      ));
      setEmployees(uSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.nameKana || a.name || "").localeCompare(b.nameKana || b.name || "", "ja")));

      const shSnap = await getDoc(doc(db, "shifts", docIdOf(companyId, yearMonth)));
      setCells(shSnap.exists() ? (shSnap.data().cells || {}) : {});
      setDirty(false);
    } catch (e) {
      console.error("シフト表の取得に失敗しました:", e);
      setError("シフト表を読み込めませんでした。権限をご確認ください。");
    } finally {
      setLoading(false);
    }
  }, [companyId, yearMonth]);

  useEffect(() => { load(); }, [load]);

  const setCell = (uid, day, key) => {
    setCells(prev => {
      const row = { ...(prev[uid] || {}) };
      if (key) row[day] = key; else delete row[day];
      return { ...prev, [uid]: row };
    });
    setDirty(true);
  };

  const save = async () => {
    setBusy("save"); setError(""); setMessage("");
    try {
      await setDoc(doc(db, "shifts", docIdOf(companyId, yearMonth)), {
        companyId, yearMonth, cells,
        updatedAt: Timestamp.now(), updatedBy: user.email,
      }, { merge: true });
      setDirty(false);
      setMessage("保存しました。給与計算では、この勤務パターンが所定労働時間として使われます。");
    } catch (e) {
      console.error("シフト表の保存に失敗しました:", e);
      setError("保存に失敗しました。権限をご確認ください。");
    } finally { setBusy(""); }
  };

  // ---- CSV ----
  const download = () => {
    const n = daysInMonth(yearMonth);
    const head = ["社員ID", "氏名", ...Array.from({ length: n }, (_, i) => String(i + 1))];
    const rows = [head];
    employees.forEach(e => {
      const row = [e.memberNo || "", e.name || e.email || ""];
      for (let d = 1; d <= n; d++) {
        const v = cells[e.id]?.[d];
        if (isCustom(v)) {
          row.push(`${v.start}-${v.end}(${Number(v.breakMin) || 0})`);
        } else {
          const p = patterns.find(x => x.key === v);
          row.push(p ? p.name : "");
        }
      }
      rows.push(row);
    });
    const BOM = "﻿";
    const csv = BOM + rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `シフト表_${yearMonth}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const upload = (file) => {
    setError(""); setMessage("");
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = String(ev.target.result).replace(/^﻿/, "");
        const lines = text.split(/\r?\n/).filter(l => l.trim());
        if (lines.length < 2) { setError("データが空です。"); return; }

        const parseLine = (line) => line.split(",").map(c => c.replace(/^"|"$/g, "").replace(/""/g, '"').trim());
        const header = parseLine(lines[0]);
        // 1列目が社員ID、2列目が氏名、3列目以降が1日〜末日
        const nameToKey = {};
        patterns.forEach(p => { nameToKey[p.name] = p.key; });

        const next = { ...cells };
        const unknown = new Set();
        let applied = 0, skipped = [];

        lines.slice(1).forEach(line => {
          const cols = parseLine(line);
          const memberNo = cols[0], name = cols[1];
          const emp = employees.find(e =>
            (memberNo && e.memberNo === memberNo) ||
            (!memberNo && name && (e.name === name || e.email === name))
          );
          if (!emp) { skipped.push(name || memberNo || "（不明）"); return; }

          const row = {};
          for (let i = 2; i < cols.length; i++) {
            const day = Number(header[i]);
            if (!day) continue;
            const v = cols[i];
            if (!v) continue;
            // 「09:05-17:35(60)」の形なら時間指定として読む
            const m = v.match(/^(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})(?:\s*\((\d+)\))?$/);
            if (m) {
              row[day] = { start: m[1], end: m[2], breakMin: Number(m[3] || 0) };
              continue;
            }
            const key = nameToKey[v];
            if (!key) { unknown.add(v); continue; }
            row[day] = key;
          }
          next[emp.id] = row;
          applied++;
        });

        setCells(next);
        setDirty(true);
        const notes = [];
        if (skipped.length > 0) notes.push(`該当する社員が見つからなかった行：${skipped.join("、")}`);
        if (unknown.size > 0)   notes.push(`知らない勤務パターン：${[...unknown].join("、")}`);
        setMessage(`${applied}名分を読み込みました。内容を確認して「保存」を押してください。` +
          (notes.length ? `\n${notes.join(" / ")}` : ""));
      } catch (e) {
        console.error("CSVの読み込みに失敗しました:", e);
        setError("CSVを読み込めませんでした。書式をご確認ください。");
      }
    };
    reader.readAsText(file, "utf-8");
    if (fileRef.current) fileRef.current.value = "";
  };

  const n = daysInMonth(yearMonth);
  const [y, m] = yearMonth.split("-").map(Number);
  const days = Array.from({ length: n }, (_, i) => {
    const d = new Date(y, m - 1, i + 1);
    return { day: i + 1, dow: d.getDay() };
  });
  const patternOf = (key) => patterns.find(p => p.key === key);

  return (
    <div style={st.container}>
      <h2 style={st.title}>🗓 シフト表</h2>
      <p style={st.lead}>
        月ごとに勤務パターンを割り当てます。ここで決めた内容が、その日の所定労働時間として給与計算に使われます。
        勤務パターンの追加や時間の変更は、給与計算の「給与設定」で行います。
      </p>

      <div style={st.toolbar}>
        <div>
          <label style={st.label}>対象年月</label>
          <input style={st.month} type="month" value={yearMonth} onChange={e => setYearMonth(e.target.value)} />
        </div>
        <div style={st.toolBtns}>
          <div style={st.viewSwitch}>
            {[{ v: "month", l: "月表示" }, { v: "day", l: "日別表示" }].map(o => (
              <button key={o.v} type="button"
                style={{ ...st.viewBtn, ...(view === o.v ? st.viewOn : {}) }}
                onClick={() => setView(o.v)}>{o.l}</button>
            ))}
          </div>
          <button style={st.dlBtn} onClick={download}>CSVをダウンロード</button>
          {canEdit && (
            <>
              <label style={st.upBtn}>
                CSVを取り込む
                <input ref={fileRef} type="file" accept=".csv" style={{ display: "none" }}
                  onChange={e => e.target.files[0] && upload(e.target.files[0])} />
              </label>
              <button style={st.saveBtn} disabled={busy !== "" || !dirty} onClick={save}>
                {busy === "save" ? "保存中..." : dirty ? "保存する" : "保存済み"}
              </button>
            </>
          )}
        </div>
      </div>

      {canEdit && (
        <div style={st.modeBar}>
          <span style={st.modeLabel}>入力方法</span>
          {[{ v: "pattern", l: "勤務パターンから選ぶ" }, { v: "time", l: "時間を指定する（5分単位）" }].map(o => (
            <button key={o.v} type="button"
              style={{ ...st.modeBtn, ...(mode === o.v ? st.modeOn : {}) }}
              onClick={() => setMode(o.v)}>{o.l}</button>
          ))}
          <span style={st.modeNote}>
            {mode === "pattern"
              ? "早番・遅番などから選びます。決まった勤務が多い職場向きです。"
              : "マスを押すとゲージが出ます。日ごとに違う時間を細かく決めたいときに使います。"}
          </span>
        </div>
      )}

      <div style={st.legend}>
        {patterns.map(p => (
          <span key={p.key} style={{ ...st.legendChip, backgroundColor: p.color || "#7f8c8d" }}>
            {p.name}{p.start ? `　${p.start}〜${p.end}` : ""}
          </span>
        ))}
      </div>

      {error   && <p style={st.error}>{error}</p>}
      {message && <p style={st.ok}>{message}</p>}
      {dirty   && <p style={st.warn}>未保存の変更があります。</p>}

      {loading ? <p style={st.empty}>読み込み中...</p>
        : employees.length === 0 ? <p style={st.empty}>承認済みの社員がいません</p>
          : view === "day" ? (
            <DayView
              yearMonth={yearMonth} day={day} setDay={setDay} daysInMonth={n}
              employees={employees} cells={cells} patterns={patterns}
              canEdit={canEdit} mode={mode}
              onPick={(uid, value) => setCell(uid, day, value)}
              onOpenGauge={(uid, value) => setEditing({ uid, day, value })}
            />
          ) : (
            <div style={st.tableWrap}>
              <table style={st.table}>
                <thead>
                  <tr>
                    <th style={{ ...st.th, ...st.thName }}>社員</th>
                    {days.map(d => (
                      <th key={d.day} style={{ ...st.thDay, ...(d.dow === 0 ? st.sun : d.dow === 6 ? st.sat : {}) }}>
                        <div>{d.day}</div>
                        <div style={st.dowLabel}>{DOW[d.dow]}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {employees.map(e => (
                    <tr key={e.id}>
                      <td style={{ ...st.td, ...st.tdName }}>
                        <div style={st.name}>{e.name || e.email}</div>
                        {e.memberNo && <div style={st.memberNo}>{e.memberNo}</div>}
                      </td>
                      {days.map(d => {
                        const v = cells[e.id]?.[d.day];
                        const p = isCustom(v) ? null : patternOf(v);
                        const bg = isCustom(v) ? "#16a085" : (p ? (p.color || "#7f8c8d") : null);
                        const text = isCustom(v) ? customLabel(v) : (p ? p.name : "－");
                        return (
                          <td key={d.day} style={st.tdCell}>
                            {canEdit && mode === "pattern" && !isCustom(v) ? (
                              <select
                                style={{
                                  ...st.cellSelect,
                                  backgroundColor: bg || "#fff",
                                  color: bg ? "#fff" : "#bbb",
                                }}
                                value={typeof v === "string" ? v : ""}
                                onChange={ev => setCell(e.id, d.day, ev.target.value)}>
                                <option value="">－</option>
                                {patterns.map(x => <option key={x.key} value={x.key}>{x.name}</option>)}
                              </select>
                            ) : canEdit ? (
                              <button type="button"
                                style={{
                                  ...st.cellBtn,
                                  backgroundColor: bg || "#fff",
                                  color: bg ? "#fff" : "#bbb",
                                }}
                                onClick={() => setEditing({
                                  uid: e.id, day: d.day,
                                  value: isCustom(v) ? { ...v } : (p && p.start
                                    ? { start: p.start, end: p.end, breakMin: p.breakMin || 0 }
                                    : { start: "09:00", end: "18:00", breakMin: 60 }),
                                })}>
                                {text}
                              </button>
                            ) : (
                              <span style={{
                                ...st.cellView,
                                backgroundColor: bg || "transparent",
                                color: bg ? "#fff" : "#ccc",
                              }}>{text}</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

      {editing && (
        <TimeGaugeModal
          value={editing.value}
          onCancel={() => setEditing(null)}
          onClear={() => { setCell(editing.uid, editing.day, ""); setEditing(null); }}
          onSave={(v) => { setCell(editing.uid, editing.day, v); setEditing(null); }}
        />
      )}

      <div style={st.help}>
        <strong>CSVの書式</strong>
        <p style={st.helpBody}>
          1行目が見出しで、1列目が社員ID、2列目が氏名、3列目以降が1日〜末日です。
          マスには勤務パターンの名前（{patterns.map(p => p.name).join("・")}）を入れます。空欄は未割当です。
          時間を直接指定する場合は <code>09:05-17:35(60)</code> のように書きます（かっこ内は休憩の分数、省略可）。<br />
          いまの内容を「CSVをダウンロード」で出し、Excelで編集してから取り込むのが確実です。
          社員は社員IDで照合し、空欄のときは氏名で照合します。
        </p>
      </div>
    </div>
  );
}

/**
 * 1日を取り出して、その日に出勤する社員の並びと時間帯を見ながら調整する表示。
 * 上に時間帯ごとの人数を出すので、手薄な時間が分かる。
 */
function DayView({ yearMonth, day, setDay, daysInMonth, employees, cells, patterns, canEdit, mode, onPick, onOpenGauge }) {
  const [y, m] = yearMonth.split("-").map(Number);
  const date = new Date(y, m - 1, day);
  const dateKey = `${y}-${pad(m)}-${pad(day)}`;

  const rangeOf = (v) => {
    if (!v) return null;
    if (isCustom(v)) return v;
    const p = patterns.find(x => x.key === v);
    return p && p.start && p.end ? p : null;
  };

  const rows = employees.map(e => {
    const v = cells[e.id]?.[day];
    return { emp: e, value: v, range: rangeOf(v), pattern: isCustom(v) ? null : patterns.find(x => x.key === v) };
  });
  const working = rows.filter(r => r.range);
  const resting = rows.filter(r => !r.range);

  // 30分ごとの人数
  const slots = Array.from({ length: 48 }, (_, i) => {
    const from = i * 30, to = from + 30;
    const count = working.filter(r => {
      const s = hhmmToMm(r.range.start), en = hhmmToMm(r.range.end);
      if (s === null || en === null) return false;
      return en > s ? (from < en && to > s) : (from < en || to > s);   // 日をまたぐ場合
    }).length;
    return { from, count };
  });
  const peak = Math.max(1, ...slots.map(x => x.count));

  const seg = (v) => {
    const s = hhmmToMm(v.start), e = hhmmToMm(v.end);
    if (s === null || e === null) return null;
    const bar = (from, to) => ({
      position: "absolute", top: "4px", bottom: "4px",
      left: `${(from / 1440) * 100}%`, width: `${((to - from) / 1440) * 100}%`,
      backgroundColor: "#16a085", borderRadius: "4px",
    });
    return e > s ? [bar(s, e)] : [bar(s, 1440), bar(0, e)];
  };

  return (
    <div>
      <div style={dv.head}>
        <button style={dv.navBtn} disabled={day <= 1} onClick={() => setDay(day - 1)}>← 前の日</button>
        <div style={dv.dateBox}>
          <span style={dv.dateMain}>{m}月{day}日</span>
          <span style={{ ...dv.dateDow, color: date.getDay() === 0 ? "#c0392b" : date.getDay() === 6 ? "#2980b9" : "#7f8c8d" }}>
            （{DOW[date.getDay()]}）
          </span>
          <span style={dv.dateCount}>出勤 {working.length}名 ／ 未割当・休み {resting.length}名</span>
        </div>
        <button style={dv.navBtn} disabled={day >= daysInMonth} onClick={() => setDay(day + 1)}>次の日 →</button>
      </div>

      <div style={dv.coverWrap}>
        <div style={dv.coverLabel}>時間帯ごとの人数</div>
        <div style={dv.coverBars}>
          {slots.map((sl, i) => (
            <div key={i} style={{
              ...dv.coverBar,
              height: `${(sl.count / peak) * 100}%`,
              backgroundColor: sl.count === 0 ? "#eef2f6" : "#2980b9",
            }} title={`${mmToHHMM(sl.from)} ${sl.count}名`} />
          ))}
        </div>
        <div style={dv.ticks}>
          {[0, 3, 6, 9, 12, 15, 18, 21, 24].map(h => (
            <span key={h} style={{ ...dv.tick, left: `${(h / 24) * 100}%` }}>{h}</span>
          ))}
        </div>
      </div>

      <div style={dv.list}>
        {rows.map(r => (
          <div key={r.emp.id} style={{ ...dv.row, ...(r.range ? {} : dv.rowOff) }}>
            <div style={dv.person}>
              <div style={dv.name}>{r.emp.name || r.emp.email}</div>
              {r.emp.memberNo && <div style={dv.memberNo}>{r.emp.memberNo}</div>}
            </div>

            <div style={dv.timeline}>
              {r.range && seg(r.range)?.map((sty, i) => <div key={i} style={sty} />)}
              {!r.range && <span style={dv.noShift}>{r.pattern ? r.pattern.name : "未割当"}</span>}
            </div>

            <div style={dv.timeText}>
              {r.range ? `${r.range.start}〜${r.range.end}${hhmmToMm(r.range.end) <= hhmmToMm(r.range.start) ? "（翌）" : ""}` : "－"}
              {r.range && <span style={dv.workText}>実働 {(workMinutesOf(r.range) / 60).toFixed(2)}h</span>}
            </div>

            {canEdit && (
              <div style={dv.edit}>
                {mode === "pattern" && !isCustom(r.value) ? (
                  <select style={dv.select} value={typeof r.value === "string" ? r.value : ""}
                    onChange={ev => onPick(r.emp.id, ev.target.value)}>
                    <option value="">未割当</option>
                    {patterns.map(x => <option key={x.key} value={x.key}>{x.name}</option>)}
                  </select>
                ) : (
                  <button type="button" style={dv.gaugeBtn}
                    onClick={() => onOpenGauge(r.emp.id, isCustom(r.value)
                      ? { ...r.value }
                      : (r.range ? { start: r.range.start, end: r.range.end, breakMin: r.range.breakMin || 0 }
                                 : { start: "09:00", end: "18:00", breakMin: 60 }))}>
                    時間を指定
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      <p style={dv.note}>
        {dateKey} の状況です。青いグラフは30分ごとの出勤人数で、手薄な時間帯がひと目で分かります。
        入力方法の切り替えは上のボタンで行います。
      </p>
    </div>
  );
}

const dv = {
  head: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", backgroundColor: "#fff", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", padding: "14px 18px", marginBottom: "12px" },
  navBtn: { padding: "8px 16px", backgroundColor: "#fff", color: "#2c3e50", border: "1px solid #ccd", borderRadius: "8px", cursor: "pointer", fontSize: "13px" },
  dateBox: { display: "flex", alignItems: "baseline", gap: "8px", flexWrap: "wrap" },
  dateMain: { fontSize: "20px", fontWeight: "bold", color: "#2c3e50" },
  dateDow: { fontSize: "14px" },
  dateCount: { fontSize: "13px", color: "#5d6d7e", marginLeft: "8px" },
  coverWrap: { backgroundColor: "#fff", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", padding: "14px 18px 26px", marginBottom: "12px", position: "relative" },
  coverLabel: { fontSize: "12px", color: "#7f8c8d", marginBottom: "6px" },
  coverBars: { display: "flex", alignItems: "flex-end", gap: "1px", height: "60px" },
  coverBar: { flex: 1, borderRadius: "2px 2px 0 0", minHeight: "2px" },
  ticks: { position: "relative", height: "14px", marginTop: "2px" },
  tick: { position: "absolute", transform: "translateX(-50%)", fontSize: "10px", color: "#95a5a6" },
  list: { backgroundColor: "#fff", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", overflow: "hidden" },
  row: { display: "flex", alignItems: "center", gap: "12px", padding: "10px 16px", borderBottom: "1px solid #f0f0f0", flexWrap: "wrap" },
  rowOff: { backgroundColor: "#fafbfc" },
  person: { minWidth: "150px" },
  name: { fontSize: "14px", fontWeight: "bold", color: "#2c3e50" },
  memberNo: { fontSize: "10px", color: "#2980b9", fontFamily: "monospace" },
  timeline: { position: "relative", flex: "1 1 260px", minWidth: "220px", height: "26px", backgroundColor: "#f0f4f8", borderRadius: "6px" },
  noShift: { position: "absolute", left: "10px", top: "5px", fontSize: "12px", color: "#bbb" },
  timeText: { minWidth: "170px", fontSize: "13px", color: "#2c3e50", fontFamily: "monospace" },
  workText: { marginLeft: "8px", color: "#16a085", fontFamily: "inherit", fontSize: "12px" },
  edit: { minWidth: "120px" },
  select: { width: "110px", padding: "6px 8px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "13px" },
  gaugeBtn: { padding: "6px 14px", backgroundColor: "#16a085", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px" },
  note: { fontSize: "12px", color: "#95a5a6", lineHeight: "1.8", marginTop: "12px" },
};

/**
 * 1本のゲージで勤務時間を決める。
 * 帯の左端をつかむと開始、右端をつかむと終了が動き、真ん中をつかむと 長さを保ったまま移動する。
 * 休憩は帯の中に別の色で表示し、開始時刻と長さを指定できる。すべて5分刻み。
 */
function TimeGaugeModal({ value, onCancel, onSave, onClear }) {
  const [start, setStart] = useState(hhmmToMm(value.start) ?? 540);
  const [len, setLen]     = useState(() => {
    const s = hhmmToMm(value.start) ?? 540, e = hhmmToMm(value.end) ?? 1080;
    return e > s ? e - s : (24 * 60 - s) + e;
  });
  const [breakMin, setBreakMin]   = useState(Number(value.breakMin) || 0);
  // 開始からの経過分。未指定なら勤務のまん中に置く（昼休憩はこの形になることが多い）
  const [breakAt, setBreakAt]     = useState(() => {
    if (value.breakAt != null) return Number(value.breakAt) || 0;
    const s0 = hhmmToMm(value.start) ?? 540, e0 = hhmmToMm(value.end) ?? 1080;
    const len0 = e0 > s0 ? e0 - s0 : (24 * 60 - s0) + e0;
    return Math.max(0, Math.round((len0 - (Number(value.breakMin) || 0)) / 2 / 5) * 5);
  });
  const barRef = useRef(null);
  const dragRef = useRef(null);

  const end = start + len;
  const v = {
    start: mmToHHMM(start), end: mmToHHMM(end % 1440),
    breakMin, breakAt: Math.min(breakAt, Math.max(0, len - breakMin)),
  };
  const overnight = end >= 1440;
  const work = Math.max(0, len - breakMin);

  // 休憩が勤務時間からはみ出さないように、表示・保存の時点で丸める
  const maxBreakAt = Math.max(0, len - breakMin);
  const bAt = Math.min(breakAt, maxBreakAt);

  const snap5 = (n) => Math.round(n / 5) * 5;
  const pctToMin = (clientX) => {
    const rect = barRef.current.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return snap5(ratio * 1440);
  };

  const onDown = (kind) => (e) => {
    e.preventDefault();
    dragRef.current = { kind, originX: e.clientX ?? e.touches?.[0]?.clientX, start, len };
  };

  useEffect(() => {
    const move = (e) => {
      const d = dragRef.current;
      if (!d || !barRef.current) return;
      const x = e.clientX ?? e.touches?.[0]?.clientX;
      if (x == null) return;
      const at = pctToMin(x);
      if (d.kind === "start") {
        const newStart = Math.min(at, d.start + d.len - 5);
        setStart(Math.max(0, newStart));
        setLen(d.start + d.len - Math.max(0, newStart));
      } else if (d.kind === "end") {
        const rawEnd = at < d.start ? at + 1440 : at;         // 日をまたぐ
        setLen(Math.max(5, Math.min(24 * 60, rawEnd - d.start)));
      } else if (d.kind === "move") {
        const rect = barRef.current.getBoundingClientRect();
        const deltaMin = snap5(((x - d.originX) / rect.width) * 1440);
        setStart(Math.min(1435, Math.max(0, d.start + deltaMin)));
      }
    };
    const up = () => { dragRef.current = null; };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  });   // eslint-disable-line react-hooks/exhaustive-deps

  // 帯の描画（日をまたぐ場合は2本に分ける）
  const pieces = overnight
    ? [[start, 1440], [0, end - 1440]]
    : [[start, end]];
  const bandStyle = (from, to, color, z) => ({
    position: "absolute", top: "6px", bottom: "6px",
    left: `${(from / 1440) * 100}%`,
    width: `${((to - from) / 1440) * 100}%`,
    backgroundColor: color, borderRadius: "5px", zIndex: z,
  });

  // 休憩の絶対位置
  const bFrom = start + bAt, bTo = bFrom + breakMin;
  const breakPieces = breakMin <= 0 ? [] :
    (bTo > 1440 ? (bFrom >= 1440 ? [[bFrom - 1440, bTo - 1440]] : [[bFrom, 1440], [0, bTo - 1440]])
                : [[bFrom, bTo]]);

  return (
    <div style={st.overlay}>
      <div style={st.modal}>
        <h3 style={st.modalTitle}>勤務時間を指定</h3>

        <div style={st.gaugeHead}>
          <span style={st.gaugeTime}>{v.start}</span>
          <span style={st.gaugeArrow}>〜</span>
          <span style={st.gaugeTime}>{v.end}{overnight && <span style={st.next}>翌日</span>}</span>
          <span style={st.gaugeWork}>実働 {(work / 60).toFixed(2)}時間</span>
        </div>

        <div style={st.barWrap} ref={barRef}>
          {pieces.map(([f, t], i) => (
            <div key={`w${i}`} style={bandStyle(f, t, "#16a085", 1)}
              onMouseDown={onDown("move")} onTouchStart={onDown("move")} />
          ))}
          {breakPieces.map(([f, t], i) => (
            <div key={`b${i}`} style={{ ...bandStyle(f, t, "#f39c12", 2), cursor: "default" }} />
          ))}
          {/* 端のつまみ */}
          <div style={{ ...st.handle, left: `${(start / 1440) * 100}%` }}
            onMouseDown={onDown("start")} onTouchStart={onDown("start")} />
          <div style={{ ...st.handle, left: `${((overnight ? end - 1440 : end) / 1440) * 100}%` }}
            onMouseDown={onDown("end")} onTouchStart={onDown("end")} />
          {[0, 6, 12, 18, 24].map(h => (
            <span key={h} style={{ ...st.tick, left: `${(h / 24) * 100}%` }}>{h}</span>
          ))}
        </div>

        <div style={st.timeInputs}>
          <div>
            <label style={st.gaugeLabel}>開始</label>
            <input type="time" step="300" value={v.start} style={st.timeInput}
              onChange={e => {
                const mm = hhmmToMm(e.target.value);
                if (mm === null) return;
                setStart(Math.round(mm / 5) * 5);
              }} />
          </div>
          <span style={st.timeSep}>〜</span>
          <div>
            <label style={st.gaugeLabel}>終了</label>
            <input type="time" step="300" value={v.end} style={st.timeInput}
              onChange={e => {
                const mm = hhmmToMm(e.target.value);
                if (mm === null) return;
                const snapped = Math.round(mm / 5) * 5;
                // 開始と同じか前なら翌日として扱う
                const newLen = snapped > start ? snapped - start : (1440 - start) + snapped;
                setLen(Math.max(5, Math.min(24 * 60, newLen)));
              }} />
          </div>
          <span style={st.timeHint}>直接入力もできます（5分単位）</span>
        </div>

        <p style={st.dragNote}>
          緑の帯の<strong>両端</strong>をつかむと開始・終了が、<strong>真ん中</strong>をつかむと長さを保ったまま前後に動きます。5分刻みです。
        </p>

        <div style={st.breakBox}>
          <div style={st.breakHead}>
            <span style={st.breakDot} />休憩
            <span style={st.breakVal}>
              {breakMin > 0 ? `${mmToHHMM((start + bAt) % 1440)}〜${mmToHHMM((start + bAt + breakMin) % 1440)}（${breakMin}分）` : "なし"}
            </span>
          </div>
          <label style={st.gaugeLabel}>
            休憩の長さ <span style={st.valueBadge}>{breakMin}分</span>
          </label>
          <div style={st.sliderRow}>
            <input type="range" min="0" max={Math.min(240, len)} step="5" value={breakMin} style={st.range}
              onChange={e => setBreakMin(Number(e.target.value))} />
            <input type="number" min="0" max={len} step="5" value={breakMin} style={st.minInput}
              onChange={e => setBreakMin(Math.max(0, Math.min(len, Math.round(Number(e.target.value || 0) / 5) * 5)))} />
            <span style={st.unit}>分</span>
          </div>
          {breakMin > 0 && (
            <>
              <label style={st.gaugeLabel}>
                休憩の開始時刻 <span style={st.valueBadge}>{mmToHHMM((start + bAt) % 1440)}</span>
              </label>
              <div style={st.sliderRow}>
                <input type="range" min="0" max={maxBreakAt} step="5" value={bAt} style={st.range}
                  onChange={e => setBreakAt(Number(e.target.value))} />
                <input type="time" step="300" value={mmToHHMM((start + bAt) % 1440)} style={st.timeInput}
                  onChange={e => {
                    const mm = hhmmToMm(e.target.value);
                    if (mm === null) return;
                    let off = Math.round(mm / 5) * 5 - start;
                    if (off < 0) off += 1440;          // 日をまたぐ勤務
                    setBreakAt(Math.max(0, Math.min(maxBreakAt, off)));
                  }} />
              </div>
              <div style={st.breakHint}>
                <span style={st.breakClock}>
                  {mmToHHMM((start + bAt) % 1440)} 〜 {mmToHHMM((start + bAt + breakMin) % 1440)}
                  {start + bAt + breakMin >= 1440 && <span style={st.next}>翌日</span>}
                </span>
                <span>（勤務開始から {Math.floor(bAt / 60)}時間{bAt % 60}分後）</span>
              </div>
            </>
          )}
        </div>

        <div style={st.modalBtns}>
          <button style={st.applyBtn} onClick={() => onSave(v)}>この時間にする</button>
          <button style={st.clearBtn} onClick={onClear}>未割当に戻す</button>
          <button style={st.cancelBtn} onClick={onCancel}>キャンセル</button>
        </div>
      </div>
    </div>
  );
}

const st = {
  container: { padding: "28px 24px", maxWidth: "100%", margin: "0 auto" },
  title: { color: "#2c3e50", marginBottom: "6px" },
  lead: { color: "#7f8c8d", fontSize: "14px", lineHeight: "1.8", marginTop: 0, marginBottom: "16px" },
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: "16px", flexWrap: "wrap", backgroundColor: "#fff", padding: "16px 20px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", marginBottom: "12px" },
  toolBtns: { display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" },
  label: { display: "block", fontSize: "13px", color: "#2c3e50", fontWeight: "bold", marginBottom: "6px" },
  month: { padding: "9px 12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "15px" },
  dlBtn: { padding: "10px 18px", backgroundColor: "#fff", color: "#2c3e50", border: "1px solid #2c3e50", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
  upBtn: { padding: "10px 18px", backgroundColor: "#2980b9", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
  saveBtn: { padding: "10px 18px", backgroundColor: "#27ae60", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
  legend: { display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" },
  legendChip: { color: "#fff", borderRadius: "20px", padding: "4px 12px", fontSize: "12px", fontWeight: "bold" },
  error: { color: "#e74c3c", fontSize: "14px", marginBottom: "10px", whiteSpace: "pre-wrap" },
  ok: { color: "#27ae60", fontSize: "14px", marginBottom: "10px", whiteSpace: "pre-wrap" },
  warn: { color: "#e67e22", fontSize: "13px", marginBottom: "10px" },
  empty: { color: "#999", textAlign: "center", marginTop: "32px" },
  tableWrap: { overflowX: "auto", backgroundColor: "#fff", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" },
  table: { borderCollapse: "collapse", fontSize: "12px" },
  th: { backgroundColor: "#2c3e50", color: "#fff", padding: "8px", fontSize: "12px", textAlign: "left", whiteSpace: "nowrap" },
  thName: { minWidth: "150px", position: "sticky", left: 0, zIndex: 1 },
  thDay: { backgroundColor: "#2c3e50", color: "#fff", padding: "5px 3px", fontSize: "11px", textAlign: "center", minWidth: "58px" },
  sun: { backgroundColor: "#7b2d2d" },
  sat: { backgroundColor: "#2d4d7b" },
  dowLabel: { fontSize: "10px", opacity: 0.85 },
  td: { borderBottom: "1px solid #eee", padding: "5px 8px" },
  tdName: { minWidth: "150px", position: "sticky", left: 0, backgroundColor: "#fff", zIndex: 1 },
  tdCell: { borderBottom: "1px solid #eee", borderLeft: "1px solid #f2f2f2", padding: "3px" },
  name: { fontWeight: "bold", color: "#2c3e50", whiteSpace: "nowrap" },
  memberNo: { fontSize: "10px", color: "#2980b9", fontFamily: "monospace" },
  cellSelect: { width: "54px", padding: "4px 2px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "11px", textAlign: "center", cursor: "pointer" },
  cellView: { display: "inline-block", width: "50px", padding: "4px 0", borderRadius: "6px", fontSize: "11px", textAlign: "center" },
  help: { marginTop: "16px", backgroundColor: "#eef2f6", borderRadius: "10px", padding: "14px 18px", fontSize: "13px", color: "#2c3e50" },
  viewSwitch: { display: "flex", gap: "4px", marginRight: "4px" },
  viewBtn: { padding: "9px 16px", border: "2px solid #ddd", borderRadius: "8px", backgroundColor: "#fff", cursor: "pointer", fontSize: "13px", color: "#7f8c8d" },
  viewOn: { border: "2px solid #2c3e50", backgroundColor: "#2c3e50", color: "#fff", fontWeight: "bold" },
  modeBar: { display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", backgroundColor: "#eef2f6", borderRadius: "10px", padding: "10px 14px", marginBottom: "12px" },
  modeLabel: { fontSize: "13px", fontWeight: "bold", color: "#2c3e50" },
  modeBtn: { padding: "6px 16px", border: "2px solid #ddd", borderRadius: "8px", backgroundColor: "#fff", cursor: "pointer", fontSize: "13px", color: "#7f8c8d" },
  modeOn: { border: "2px solid #2c3e50", backgroundColor: "#2c3e50", color: "#fff", fontWeight: "bold" },
  modeNote: { fontSize: "12px", color: "#5d6d7e", flexBasis: "100%", lineHeight: "1.7" },
  cellBtn: { width: "54px", padding: "5px 2px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "10px", cursor: "pointer", whiteSpace: "nowrap", overflow: "hidden" },
  overlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "flex-start", zIndex: 1000, overflowY: "auto", padding: "60px 0" },
  modal: { backgroundColor: "#fff", padding: "28px 32px", borderRadius: "16px", width: "480px", maxWidth: "92vw" },
  modalTitle: { color: "#2c3e50", margin: "0 0 16px", fontSize: "18px" },
  gaugeHead: { display: "flex", alignItems: "baseline", gap: "10px", flexWrap: "wrap", marginBottom: "10px" },
  gaugeTime: { fontSize: "22px", fontWeight: "bold", color: "#2c3e50", fontFamily: "monospace" },
  gaugeArrow: { color: "#7f8c8d" },
  next: { fontSize: "11px", color: "#e67e22", marginLeft: "6px" },
  gaugeWork: { marginLeft: "auto", fontSize: "14px", color: "#16a085", fontWeight: "bold" },
  barWrap: { position: "relative", height: "42px", backgroundColor: "#f0f4f8", borderRadius: "6px", marginBottom: "18px", userSelect: "none", touchAction: "none" },
  handle: { position: "absolute", top: "0", bottom: "0", width: "12px", marginLeft: "-6px", cursor: "ew-resize", zIndex: 3, backgroundColor: "rgba(44,62,80,0.85)", borderRadius: "3px" },
  dragNote: { fontSize: "12px", color: "#5d6d7e", lineHeight: "1.8", marginTop: 0, marginBottom: "14px" },
  timeInputs: { display: "flex", alignItems: "flex-end", gap: "10px", flexWrap: "wrap", marginBottom: "12px" },
  timeInput: { padding: "8px 10px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "16px", fontFamily: "monospace" },
  timeSep: { color: "#7f8c8d", paddingBottom: "10px" },
  timeHint: { fontSize: "11px", color: "#95a5a6", paddingBottom: "12px" },
  breakBox: { backgroundColor: "#fff9ec", border: "1px solid #f5d089", borderRadius: "10px", padding: "12px 14px" },
  breakHead: { display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: "bold", color: "#8a5a00", marginBottom: "6px" },
  breakDot: { width: "12px", height: "12px", borderRadius: "3px", backgroundColor: "#f39c12", display: "inline-block" },
  breakVal: { marginLeft: "auto", fontFamily: "monospace", fontWeight: "normal", color: "#5d6d7e" },
  breakHint: { display: "flex", alignItems: "baseline", gap: "8px", flexWrap: "wrap", fontSize: "12px", color: "#95a5a6", marginTop: "4px" },
  sliderRow: { display: "flex", alignItems: "center", gap: "10px" },
  minInput: { width: "80px", padding: "7px 9px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "15px", textAlign: "right", fontFamily: "monospace" },
  unit: { fontSize: "13px", color: "#7f8c8d" },
  valueBadge: { marginLeft: "6px", fontFamily: "monospace", fontSize: "15px", color: "#8a5a00" },
  breakClock: { fontSize: "15px", fontWeight: "bold", color: "#8a5a00", fontFamily: "monospace" },
  tick: { position: "absolute", bottom: "-16px", transform: "translateX(-50%)", fontSize: "10px", color: "#95a5a6" },
  gaugeLabel: { display: "block", fontSize: "13px", color: "#2c3e50", fontWeight: "bold", marginTop: "14px", marginBottom: "4px" },
  range: { width: "100%" },
  breakInput: { width: "110px", padding: "8px 10px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px", textAlign: "right" },
  gaugeNote: { fontSize: "12px", color: "#95a5a6", lineHeight: "1.8", marginTop: "14px", marginBottom: 0 },
  modalBtns: { display: "flex", gap: "10px", marginTop: "18px", flexWrap: "wrap" },
  applyBtn: { padding: "10px 24px", backgroundColor: "#16a085", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
  clearBtn: { padding: "10px 18px", backgroundColor: "#fff", color: "#7f8c8d", border: "1px solid #ccd", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
  cancelBtn: { padding: "10px 18px", backgroundColor: "#95a5a6", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
  helpBody: { color: "#5d6d7e", lineHeight: "1.8", marginTop: "6px", marginBottom: 0 },
};

export default ShiftTable;
